import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './App.css';

const notificationMessages = [
  { text: 'Lauren just got her home insulated', icon: '🏠' },
  { text: 'Only 7 spaces left this week', icon: '⏰' },
  { text: 'Mike from Aurora booked his free diagnostic', icon: '✅' },
  { text: '5 spots remaining for next week', icon: '📅' },
  { text: 'Sarah just saved $200/month on energy bills', icon: '💰' },
  { text: 'Limited availability - 3 spots left', icon: '🔥' }
];

function App() {
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get('location') || 'Denver';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    issue: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const heroSlides = [
    {
      image: '/images/hero-1.jpg',
      title: 'Professional Insulation Installation',
      subtitle: 'Expert team installing quality insulation for maximum energy efficiency'
    },
    {
      image: '/images/hero-2.jpg',
      title: 'Expert Consultation & Planning',
      subtitle: 'Our team works with you to create the perfect insulation solution'
    },
    {
      image: '/images/hero-3.jpg',
      title: 'Advanced Spray Foam Technology',
      subtitle: 'State-of-the-art insulation methods for superior thermal performance'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  useEffect(() => {
    const showRandomNotification = () => {
      const randomMessage = notificationMessages[Math.floor(Math.random() * notificationMessages.length)];
      setNotificationMessage(randomMessage);
      setShowNotification(true);
      
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    };

    // Show first notification after 5 seconds
    const initialTimer = setTimeout(() => {
      showRandomNotification();
    }, 5000);

    // Then show every 30 seconds
    const interval = setInterval(() => {
      showRandomNotification();
    }, 30000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
    setSubmitted(true);
    // In production, send to your backend/CRM
  };

  return (
    <div className="App">
      {/* Header with Logo */}
      <header className="site-header">
        <div className="container">
          <div className="header-content">
            <img 
              src="/images/logo.png" 
              alt="Level Up Insulation" 
              className="logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="header-location">
              Serving {locationParam}
            </div>
            <a href="tel:+17204384271" className="btn header-call-btn">
              <i className="fa-solid fa-phone"></i> Call
            </a>
            <a href="#form" className="btn btn-primary header-cta-btn">
              Free Estimate
            </a>
          </div>
        </div>
      </header>

      {/* Notification Popup */}
      {showNotification && (
        <div className="notification-popup">
          <div className="notification-content">
            <span className="notification-icon">{notificationMessage.icon}</span>
            <span className="notification-text">{notificationMessage.text}</span>
            <button 
              className="notification-close"
              onClick={() => setShowNotification(false)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="hero-slides">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          ))}
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="badge">FREE DIAGNOSTIC PACKAGE</div>
            <h1 className="hero-title">
              Stop Guessing Where Your Home Loses Heat
            </h1>
            <p className="hero-subtitle">
              Get a free thermal imaging scan that shows exactly where heat escapes, 
              plus a prioritized fix plan. Installation guaranteed within 7 days or $300 credit.
            </p>
            <div className="hero-cta">
              <a href="#form" className="btn btn-primary">
                Claim Your Free Diagnostic
              </a>
              <p className="hero-guarantee">✓ No obligation • ✓ Free scan • ✓ 7-day guarantee</p>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">$0</div>
                <div className="stat-label">Cost</div>
              </div>
              <div className="stat">
                <div className="stat-number">7 Days</div>
                <div className="stat-label">Installation Guarantee</div>
              </div>
              <div className="stat">
                <div className="stat-number">100%</div>
                <div className="stat-label">Visual Proof</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof">
        <div className="container">
          <div className="social-proof-content">
            <div className="stars">
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
              <span className="star">⭐</span>
            </div>
            <p className="social-proof-text">
              <strong>4.9/5</strong> from <strong>247+</strong> satisfied homeowners in {locationParam}
            </p>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section 
        className="problem"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="shape-bottom"></div>
        <div className="container">
          <h2 className="section-title">Tired of Cold Rooms and High Energy Bills?</h2>
          <div className="problem-grid">
            <div className="problem-item">
              <div className="problem-icon">❄️</div>
              <h3>Cold Rooms</h3>
              <p>One or more rooms stay cold no matter how high you turn the heat</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">💨</div>
              <h3>Drafts & Uneven Temperatures</h3>
              <p>Feeling drafts or noticing some areas are much warmer than others</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">💰</div>
              <h3>Rising Energy Bills</h3>
              <p>Winter heating costs keep climbing with no clear reason why</p>
            </div>
            <div className="problem-item">
              <div className="problem-icon">🤷</div>
              <h3>Guessing Game</h3>
              <p>Not sure where to start or if insulation will actually fix the problem</p>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Work Showcase Section */}
      <section className="work-showcase">
        <div className="container">
          <h2 className="section-title">Our Work in Action</h2>
          <p className="section-subtitle">
            See our expert team in action, delivering professional insulation solutions across {locationParam}
          </p>
          <div className="showcase-grid">
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-1.jpg" 
                  alt="Professional insulation installation" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Expert Installation</h3>
                <p>Professional team installing quality insulation with precision and care</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-2.jpg" 
                  alt="Expert consultation" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Personal Consultation</h3>
                <p>Working with you to create the perfect insulation solution for your home</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-3.jpg" 
                  alt="Advanced spray foam technology" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Advanced Technology</h3>
                <p>State-of-the-art spray foam insulation methods for superior thermal performance</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <div className="section-divider"></div>
      <section className="solution">
        <div className="container">
          <h2 className="section-title">Visual Proof Beats Guesswork</h2>
          <p className="section-subtitle">
            Most heat loss isn't where homeowners think it is. Our thermal imaging technology 
            shows you exactly what's happening, before you spend a dollar.
          </p>
          <div className="solution-wrapper">
            <div className="solution-grid">
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-2.jpg" alt="Thermal imaging scan" className="solution-image" />
                <div className="solution-number">01</div>
              </div>
              <div className="solution-content">
                <h3>Free Thermal Imaging Scan</h3>
                <p>Advanced thermal cameras identify heat loss areas invisible to the naked eye</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-4.jpg" alt="Insulation efficiency" className="solution-image" />
                <div className="solution-number">02</div>
              </div>
              <div className="solution-content">
                <h3>Insulation Efficiency Score</h3>
                <p>Get a clear score showing your home's current insulation performance</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-3.jpg" alt="Energy savings" className="solution-image" />
                <div className="solution-number">03</div>
              </div>
              <div className="solution-content">
                <h3>Personalized Savings Projection</h3>
                <p>See estimated energy bill savings based on your specific home and usage</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-1.jpg" alt="Fix plan" className="solution-image" />
                <div className="solution-number">04</div>
              </div>
              <div className="solution-content">
                <h3>Prioritized Fix Plan</h3>
                <p>Know exactly what to fix first. Perfect for budget-conscious homeowners</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-5.jpg" alt="Installation guarantee" className="solution-image" />
                <div className="solution-number">05</div>
              </div>
              <div className="solution-content">
                <h3>7-Day Installation Guarantee</h3>
                <p>Installation starts within 7 days of your scan, or receive $300 credit</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-6.jpg" alt="No obligation" className="solution-image" />
                <div className="solution-number">06</div>
              </div>
              <div className="solution-content">
                <h3>No Obligation</h3>
                <p>Get your report and decide when you're ready. No pushy sales tactics</p>
              </div>
            </div>
            </div>
            <div className="solution-customer-image">
              <img 
                src="/images/customer-1.jpg" 
                alt="Happy customer" 
                className="customer-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="customer-name">
                <p className="customer-name-text">Joanne Roarke</p>
                <p className="customer-label">Happy Customer</p>
              </div>
              <a href="#form" className="btn btn-primary customer-cta-btn">
                Get Your Free Estimate →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form Section */}
      <section id="form" className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-content">
              <h2>Claim Your Free Diagnostic Package</h2>
              <p className="form-subtitle">
                Limited weekly capacity. Book your free thermal imaging scan today.
              </p>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="lead-form">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(303) 555-1234"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Property Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder={`123 Main St, ${locationParam}, CO`}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="issue">What's your main concern? (Optional)</label>
                    <select
                      id="issue"
                      name="issue"
                      value={formData.issue}
                      onChange={handleChange}
                    >
                      <option value="">Select an option</option>
                      <option value="cold-rooms">Cold rooms</option>
                      <option value="drafts">Drafts or uneven temperatures</option>
                      <option value="high-bills">High energy bills</option>
                      <option value="general">General comfort issues</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary btn-large">
                    Book My Free Diagnostic
                  </button>
                  <p className="form-disclaimer">
                    By submitting, you agree to be contacted about your free diagnostic. 
                    No obligation. We respect your privacy.
                  </p>
                </form>
              ) : (
                <div className="form-success">
                  <div className="success-icon">✓</div>
                  <h3>Thank You!</h3>
                  <p>We've received your request. Our team will contact you within 5 minutes to schedule your free diagnostic.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee">
        <div className="container">
          <div className="guarantee-content">
            <div className="guarantee-badge">OUR GUARANTEE</div>
            <h2>7-Day Installation Guarantee</h2>
            <p className="guarantee-text">
              We guarantee that insulation installation will begin within 7 days of your 
              heat loss scan completion. If we can't start within 7 days, you'll receive 
              a $300 credit toward your installation.
            </p>
            <p className="guarantee-disclaimer">*Terms and conditions apply. Availability varies by location.</p>
            <div className="guarantee-features">
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>Fast response within 5 minutes</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>Same-day booking attempts</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>7-day installation start guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="different">
        <div className="container">
          <h2 className="section-title">Why Level Up Insulation is Different</h2>
          <div className="different-grid">
            <div className="different-item">
              <h3>Visual Proof, Not Guesswork</h3>
              <p>See exactly where heat escapes with thermal imaging. Most competitors don't offer this</p>
            </div>
            <div className="different-item">
              <h3>Fix the Biggest Problems First</h3>
              <p>Get a prioritized plan so you can tackle upgrades in stages if needed</p>
            </div>
            <div className="different-item">
              <h3>No Pushy Sales</h3>
              <p>Get your free report and decide when you're ready. No obligation</p>
            </div>
            <div className="different-item">
              <h3>Installation Time Guarantee</h3>
              <p>We're the only ones offering a 7-day installation guarantee with $300 credit</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>Is the diagnostic really free?</h3>
              <p>Yes, absolutely. The thermal imaging scan, report, and fix plan are completely free with no obligation to purchase anything.</p>
            </div>
            <div className="faq-item">
              <h3>How long does the scan take?</h3>
              <p>The thermal imaging scan typically takes 30-60 minutes, depending on the size of your home.</p>
            </div>
            <div className="faq-item">
              <h3>What will I receive in the report?</h3>
              <p>You'll get an insulation efficiency score, personalized energy savings projection, leak detection results, and a prioritized fix plan showing what to address first.</p>
            </div>
            <div className="faq-item">
              <h3>Do I have to book installation after the scan?</h3>
              <p>No. There's no obligation. You'll receive your report and can decide when you're ready to proceed with installation.</p>
            </div>
            <div className="faq-item">
              <h3>What if you can't start installation within 7 days?</h3>
              <p>If we can't begin installation within 7 days of your scan, you'll receive a $300 credit toward your installation.</p>
            </div>
            <div className="faq-item">
              <h3>Do you service my area?</h3>
              <p>We service {locationParam} and surrounding suburbs. Enter your address in the form above and we'll confirm availability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Serving {locationParam} & Surrounding Areas</h2>
          <p className="section-subtitle">
            We provide professional insulation services throughout the {locationParam} metro area
          </p>
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196281.1291475455!2d-105.1099275!3d39.7392358!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b80aa231f17aa%3A0x620436728b3a4514!2sDenver%2C%20CO!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`${locationParam} Service Area Map`}
            ></iframe>
          </div>
          <div className="service-areas">
            <h3>Service Areas</h3>
            <div className="areas-grid">
              <div className="area-item">Denver</div>
              <div className="area-item">Aurora</div>
              <div className="area-item">Lakewood</div>
              <div className="area-item">Westminster</div>
              <div className="area-item">Arvada</div>
              <div className="area-item">Thornton</div>
              <div className="area-item">Centennial</div>
              <div className="area-item">Broomfield</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        className="final-cta"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="final-cta-overlay"></div>
        <div className="container">
          <h2>Ready to Stop Guessing?</h2>
          <p>Get visual proof of where your home loses heat. Completely free, no obligation.</p>
          <a href="#form" className="btn btn-primary btn-large">
            Claim Your Free Diagnostic Now
          </a>
          <p className="final-cta-note">Limited weekly capacity • Book today to secure your spot</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>Level Up Insulation</h3>
              <p>{locationParam}'s trusted insulation experts</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Services</h4>
                <a href="#form">Free Diagnostic</a>
                <a href="#form">Insulation Installation</a>
                <a href="#form">Heat Loss Analysis</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#form">About Us</a>
                <a href="#form">Contact</a>
                <a href="#form">Privacy Policy</a>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <p><i className="fa-solid fa-phone"></i> <a href="tel:+17204384271">(720) 438-4271</a></p>
                <p><i className="fa-solid fa-envelope"></i> <a href="mailto:info@levelupinsulationco.com">info@levelupinsulationco.com</a></p>
                <p><i className="fa-solid fa-location-dot"></i> 6850 Broadway St, Denver, CO 80221</p>
              </div>
              <div className="footer-column">
                <h4>Hours</h4>
                <p>Mon-Fri: 8:00 AM - 5:00 PM</p>
                <p>Sat: 8:00 AM - 5:00 PM</p>
                <p>Sun: Closed</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Level Up Insulation. All rights reserved.</p>
            <p className="footer-link"><a href="https://levelupinsulationco.com">levelupinsulationco.com</a></p>
          </div>
        </div>
      </footer>

      {/* Floating Phone Button */}
      <a href="tel:+17204384271" className="floating-phone-btn" aria-label="Call us">
        <i className="fa-solid fa-phone"></i>
      </a>
    </div>
  );
}

export default App;

